import { type FormikProps } from 'formik';
import { AnnouncementAudienceType } from '@/app/enums/AnnouncementAudienceType';
import { ANNOUNCEMENT_TYPE_DESCRIPTION, AnnouncementType } from '@/app/enums/AnnouncementType';
import AnnouncementRichTextEditor from '@/app/pages/notifications/announcements/components/AnnouncementRichTextEditor';
import AnnouncementRoleSelect from '@/app/pages/notifications/announcements/components/AnnouncementRoleSelect';
import AnnouncementUserSelect from '@/app/pages/notifications/announcements/components/AnnouncementUserSelect';
import FieldError from '@/app/pages/notifications/announcements/components/FieldError';
import {
  type AnnouncementFormValues,
  type AnnouncementMediaUploadEntry,
  type AnnouncementMediaEnqueue,
} from '@/app/pages/notifications/announcements/core/_models';

type AnnouncementFormFieldsProps = {
  formik: FormikProps<AnnouncementFormValues>;
  disabled: boolean;
  uploadEntries: AnnouncementMediaUploadEntry[];
  onMediaSelected: AnnouncementMediaEnqueue;
  onRetryMediaUpload: (id: string) => void;
  onRemoveMediaUpload: (id: string) => void;
};

const TYPE_DESCRIPTIONS: Record<AnnouncementType, string> = {
  [AnnouncementType.MANDATORY]: 'Bloqueia o admin até ele confirmar a ciência do comunicado.',
  [AnnouncementType.DISMISSIBLE]: 'Pode ser fechado sem confirmação.',
};

const AUDIENCE_DESCRIPTIONS: Record<AnnouncementAudienceType, string> = {
  [AnnouncementAudienceType.ALL]: 'Todos os usuários do ambiente recebem o comunicado.',
  [AnnouncementAudienceType.ROLES]: 'Somente usuários com as funções escolhidas.',
  [AnnouncementAudienceType.USERS]: 'Somente os usuários escolhidos.',
};

const AUDIENCE_LABELS: Record<AnnouncementAudienceType, string> = {
  [AnnouncementAudienceType.ALL]: 'Todos',
  [AnnouncementAudienceType.ROLES]: 'Funções',
  [AnnouncementAudienceType.USERS]: 'Usuários',
};

const AUDIENCE_ICONS: Record<AnnouncementAudienceType, string> = {
  [AnnouncementAudienceType.ALL]: 'bi-people-fill',
  [AnnouncementAudienceType.ROLES]: 'bi-person-badge',
  [AnnouncementAudienceType.USERS]: 'bi-person',
};

const OPTION_CLASS_NAME =
  'announcement-option d-flex flex-center position-relative my-0 me-0 fs-7 fw-medium text-nowrap cursor-pointer';

function AnnouncementFormFields({
  formik,
  disabled,
  uploadEntries,
  onMediaSelected,
  onRetryMediaUpload,
  onRemoveMediaUpload,
}: AnnouncementFormFieldsProps) {
  const { values, errors, setFieldValue } = formik;
  const changeBody = (body: string) => {
    void setFieldValue('body', body, false);
  };

  return (
    <>
      <div className="announcement-form-content d-flex flex-column overflow-y-auto">
        <div>
          <label className="form-label fs-6 fw-bold text-gray-700 required" htmlFor="announcement_title">
            Título
          </label>
          <input
            className="form-control"
            disabled={disabled}
            id="announcement_title"
            maxLength={255}
            name="title"
            placeholder="Digite um título para o comunicado..."
            type="text"
            value={values.title}
            onChange={formik.handleChange}
          />
          <FieldError message={errors.title} />
        </div>

        <div className="d-flex flex-column flex-column-fluid">
          <AnnouncementRichTextEditor
            disabled={disabled}
            inputId="announcement_body"
            label="Mensagem"
            uploadEntries={uploadEntries}
            value={values.body}
            onChange={changeBody}
            onMediaSelected={onMediaSelected}
            onRemoveMediaUpload={onRemoveMediaUpload}
            onRetryMediaUpload={onRetryMediaUpload}
          />
          <FieldError message={errors.body} />
        </div>
      </div>

      <div className="announcement-form-settings d-flex flex-column overflow-y-auto bg-gray-100">
        <fieldset>
          <legend className="form-label fs-6 fw-bold text-gray-700 required">Tipo</legend>
          <div className="announcement-options d-grid">
            {Object.values(AnnouncementType).map((type) => {
              const typeIconClassName = type === AnnouncementType.MANDATORY ? 'bi-lock' : 'bi-bell';
              return (
                <label key={type} className={OPTION_CLASS_NAME} htmlFor={`announcement_type_${type}`}>
                  <input
                    aria-describedby="announcement_type_help"
                    aria-labelledby={`announcement_type_${type}_label`}
                    checked={values.type === type}
                    className="visually-hidden"
                    disabled={disabled}
                    id={`announcement_type_${type}`}
                    name="type"
                    type="radio"
                    onChange={() => setFieldValue('type', type)}
                  />
                  <i aria-hidden="true" className={`bi text-reset ${typeIconClassName}`} />
                  <span id={`announcement_type_${type}_label`}>{ANNOUNCEMENT_TYPE_DESCRIPTION[type]}</span>
                </label>
              );
            })}
          </div>
          <div className="form-text" id="announcement_type_help">
            {TYPE_DESCRIPTIONS[values.type]}
          </div>
        </fieldset>

        <fieldset>
          <legend className="form-label fs-6 fw-bold text-gray-700 required">Público</legend>
          <div className="announcement-options announcement-audience-options d-grid">
            {Object.values(AnnouncementAudienceType).map((audienceType) => (
              <label key={audienceType} className={OPTION_CLASS_NAME} htmlFor={`announcement_audience_${audienceType}`}>
                <input
                  aria-describedby="announcement_audience_help"
                  aria-labelledby={`announcement_audience_${audienceType}_label`}
                  checked={values.audienceType === audienceType}
                  className="visually-hidden"
                  disabled={disabled}
                  id={`announcement_audience_${audienceType}`}
                  name="audienceType"
                  type="radio"
                  onChange={() => setFieldValue('audienceType', audienceType)}
                />
                <i aria-hidden="true" className={`bi text-reset ${AUDIENCE_ICONS[audienceType]}`} />
                <span id={`announcement_audience_${audienceType}_label`}>{AUDIENCE_LABELS[audienceType]}</span>
              </label>
            ))}
          </div>
          <div className="form-text" id="announcement_audience_help">
            {AUDIENCE_DESCRIPTIONS[values.audienceType]}
          </div>
          {values.audienceType === AnnouncementAudienceType.ROLES ? (
            <div className="mt-3">
              <label className="visually-hidden" htmlFor="announcement_roles">
                Funções escolhidas
              </label>
              <AnnouncementRoleSelect
                disabled={disabled}
                inputId="announcement_roles"
                value={values.userRoleIds}
                onChange={(userRoleIds) => setFieldValue('userRoleIds', userRoleIds)}
              />
              <FieldError message={errors.userRoleIds as string | undefined} />
            </div>
          ) : null}
          {values.audienceType === AnnouncementAudienceType.USERS ? (
            <div className="mt-3">
              <label className="visually-hidden" htmlFor="announcement_users">
                Usuários escolhidos
              </label>
              <AnnouncementUserSelect
                disabled={disabled}
                inputId="announcement_users"
                value={values.userIds}
                onChange={(userIds) => setFieldValue('userIds', userIds)}
              />
              <FieldError message={errors.userIds as string | undefined} />
            </div>
          ) : null}
        </fieldset>

        <div className="d-flex align-items-center justify-content-between gap-3 border-top pt-5">
          <div>
            <label className="form-label fs-6 fw-bold text-gray-700 mb-0" htmlFor="announcement_email_enabled">
              Enviar também por e-mail
            </label>
            <div className="form-text mt-0" id="announcement_email_enabled_help">
              Quando o comunicado entrar em vigor, o público recebe o aviso também por e-mail.
            </div>
          </div>
          <div className="form-check form-switch form-check-custom form-check-solid">
            <input
              aria-describedby="announcement_email_enabled_help"
              checked={values.emailEnabled}
              className="form-check-input"
              disabled={disabled}
              id="announcement_email_enabled"
              name="emailEnabled"
              type="checkbox"
              onChange={formik.handleChange}
            />
          </div>
        </div>

        <section className="border-top pt-5">
          <h3 className="d-flex align-items-center gap-3 fs-6 fw-semibold mb-4">
            <i aria-hidden="true" className="bi bi-calendar3 text-muted fs-4" />
            <span>Agendamento</span>
          </h3>
          <div className="d-flex flex-column gap-5">
            <fieldset>
              <legend className="visually-hidden">Publicação</legend>
              <div className="announcement-options d-grid">
                <label className={OPTION_CLASS_NAME} htmlFor="announcement_starts_now">
                  <input
                    checked={values.startsNow}
                    className="visually-hidden"
                    disabled={disabled}
                    id="announcement_starts_now"
                    name="startsNow"
                    type="radio"
                    onChange={() => setFieldValue('startsNow', true)}
                  />
                  <i aria-hidden="true" className="bi bi-lightning-charge text-reset" /> Publicar agora
                </label>
                <label className={OPTION_CLASS_NAME} htmlFor="announcement_scheduled">
                  <input
                    checked={!values.startsNow}
                    className="visually-hidden"
                    disabled={disabled}
                    id="announcement_scheduled"
                    name="startsNow"
                    type="radio"
                    onChange={() => setFieldValue('startsNow', false)}
                  />
                  <i aria-hidden="true" className="bi bi-calendar2-week text-reset" /> Agendar
                </label>
              </div>
            </fieldset>
            <div>
              {values.startsNow ? (
                <>
                  <div className="form-label fs-6 fw-bold text-gray-700">Início</div>
                  <div className="form-control border-dashed d-flex align-items-center gap-2 text-muted">
                    <i aria-hidden="true" className="bi bi-lightning-charge" /> Assim que você publicar
                  </div>
                  <div className="form-text">Escolha “Agendar” para definir outra data.</div>
                </>
              ) : (
                <>
                  <label className="form-label fs-6 fw-bold text-gray-700 required" htmlFor="announcement_start">
                    Início
                  </label>
                  <input
                    aria-describedby="announcement_start_help"
                    className="form-control"
                    disabled={disabled}
                    id="announcement_start"
                    name="startDate"
                    type="datetime-local"
                    value={values.startDate}
                    onChange={formik.handleChange}
                  />
                  <div className="form-text" id="announcement_start_help">
                    O comunicado aparece a partir desta data.
                  </div>
                  <FieldError message={errors.startDate} />
                </>
              )}
            </div>
            <div>
              <label className="form-label fs-6 fw-bold text-gray-700" htmlFor="announcement_end">
                Fim (opcional)
              </label>
              <input
                aria-describedby="announcement_end_help"
                className="form-control"
                disabled={disabled}
                id="announcement_end"
                name="endDate"
                type="datetime-local"
                value={values.endDate}
                onChange={formik.handleChange}
              />
              <div className="form-text" id="announcement_end_help">
                Deixe em branco para não definir uma data de fim.
              </div>
              <FieldError message={errors.endDate} />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default AnnouncementFormFields;
